import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PaginationMeta } from '@/types';

type PaginationProps = {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
};

export function Pagination({ meta, onPageChange }: PaginationProps) {
  const { page, total, total_pages, per_page } = meta;

  if (total_pages <= 1) return null;

  const from = (page - 1) * per_page + 1;
  const to = Math.min(page * per_page, total);

  return (
    <div className="flex items-center justify-between border-t border-border px-6 py-3">
      <p className="text-sm text-muted-foreground">
        Mostrando {from}-{to} de {total}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className={cn(
            'rounded-md p-1.5 text-muted-foreground transition-colors',
            page > 1 ? 'hover:bg-accent hover:text-accent-foreground' : 'opacity-40',
          )}
          aria-label="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {Array.from({ length: total_pages }, (_, i) => i + 1)
          .filter((p) => {
            if (total_pages <= 7) return true;
            if (p === 1 || p === total_pages) return true;
            if (Math.abs(p - page) <= 1) return true;
            return false;
          })
          .map((p, idx, arr) => {
            const prev = arr[idx - 1];
            const showEllipsis = prev !== undefined && p - prev > 1;
            return (
              <span key={p} className="flex items-center">
                {showEllipsis && <span className="px-1 text-sm text-muted-foreground">...</span>}
                <button
                  onClick={() => onPageChange(p)}
                  className={cn(
                    'min-w-[32px] rounded-md px-2 py-1 text-sm font-medium transition-colors',
                    p === page
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  {p}
                </button>
              </span>
            );
          })}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= total_pages}
          className={cn(
            'rounded-md p-1.5 text-muted-foreground transition-colors',
            page < total_pages ? 'hover:bg-accent hover:text-accent-foreground' : 'opacity-40',
          )}
          aria-label="Página siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
